import { Info } from "lucide-react";
import React, { Dispatch } from "react";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgIconButton,
  IgLink,
  IgSelect,
  IgTypography,
} from "@intelligems/ig-theme";
import { StripeApiConvertToFlexibleResponseBodyType } from "@intelligems/ig-types";
import { Grid, MenuItem } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { usePostStripeApi } from "@src/api/stripe/stripe-api";
import { AreYouSure } from "@src/components/Popover/AreYouSure";
import { IgAlertSimple } from "@src/components/Utils/IgAlertSimple";
import { AutoScaleSwitch } from "@src/features/admin/components/Fields/AutoScaleSwitch";
import {
  CreateOrModifySubscriptionReducerType,
  CreateSubscriptionReducerAction,
} from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/subscriptionReducer";
import { useIsMutating } from "@tanstack/react-query";

import { SaveRow } from "../SaveRow/SaveRow";

interface SettingsTabProps {
  payload: CreateOrModifySubscriptionReducerType;
  dispatchPayload: Dispatch<CreateSubscriptionReducerAction>;
}

export const SettingsSection = ({
  payload,
  dispatchPayload,
}: SettingsTabProps) => {
  const convertSub =
    usePostStripeApi<StripeApiConvertToFlexibleResponseBodyType>();

  const loading = useIsMutating({ mutationKey: ["postStripeApi"] }) > 0;
  const [help, setHelp] = useImmer<boolean>(false);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Grid item>
        <IgTypography variant={"h6"}>Settings:</IgTypography>
      </Grid>
      <Grid container item direction={"column"} rowSpacing={2}>
        {Object.entries(payload.warnings).map(([key, value]) =>
          value ? (
            <Grid item key={key}>
              <IgAlertSimple
                severity={"warning"}
                message={value}
                alertKey={key}
              />
            </Grid>
          ) : null
        )}
        {payload.mode === "create" ? (
          <Grid item container direction={"column"}>
            <Grid item>
              <IgSelect
                label={"Billing Provider"}
                sx={{ width: "200px" }}
                disabled={loading}
                value={payload.billingProvider}
                onChange={(e) => {
                  dispatchPayload({
                    type: "setBillingProvider",
                    value: e.target.value,
                  });
                }}
              >
                <MenuItem value={"stripe"}>Stripe</MenuItem>
                {payload.agencyId ? null : (
                  <MenuItem value={"shopify"}>Shopify</MenuItem>
                )}
              </IgSelect>
            </Grid>
          </Grid>
        ) : null}
        <Grid item>
          <AutoScaleSwitch
            disabled={loading}
            checked={payload.settings.autoScale || false}
            onClick={() => {
              dispatchPayload({
                type: "setAutoScale",
                value: !payload.settings.autoScale,
              });
            }}
          />
        </Grid>
        <Grid item>
          <IgSelect
            label={"Collection Method"}
            sx={{ width: "200px" }}
            disabled={loading}
            value={payload.settings.collectionMethod}
            onChange={(e) => {
              dispatchPayload({
                type: "setCollectionMethod",
                value: e.target.value,
              });
            }}
          >
            <MenuItem value={"chargeAutomatically"}>
              Charge Automatically
            </MenuItem>
            <MenuItem value={"sendInvoice"}>Send Invoice</MenuItem>
          </IgSelect>
        </Grid>
        {/*{payload.mode === "modify" ? (*/}
        {/*  <Grid item>*/}
        {/*    <IgTypography>{`Billing Mode: ${payload.settings.billingMode}`}</IgTypography>*/}
        {/*    {payload.settings.billingMode === "classic" ? (*/}
        {/*      <AreYouSure*/}
        {/*        keyId={"convert-to-flexible"}*/}
        {/*        enabled={true}*/}
        {/*        isLoading={convertSub.isPending}*/}
        {/*        onYesClick={() => {*/}
        {/*          convertSub.mutate(*/}
        {/*            {*/}
        {/*              orgs: payload.orgs,*/}
        {/*              action: "convertToFlexible",*/}
        {/*              stripeSubscriptionId: payload.stripeSubscriptionId,*/}
        {/*            },*/}
        {/*            {*/}
        {/*              onSuccess: (data) => {*/}
        {/*                window.location.reload();*/}
        {/*              },*/}
        {/*            }*/}
        {/*          );*/}
        {/*        }}*/}
        {/*      >*/}
        {/*        <IgButton>Convert to Flexible</IgButton>*/}
        {/*      </AreYouSure>*/}
        {/*    ) : null}*/}
        {/*  </Grid>*/}
        {/*) : null}*/}
        {payload.mode === "modify" ? (
          <Grid container item>
            <Grid item>
              <IgSelect
                label={"Proration Behavior"}
                sx={{ width: "200px" }}
                disabled={loading}
                value={payload.settings.prorationBehavior}
                onChange={(e) => {
                  dispatchPayload({
                    type: "setProrationBehavior",
                    value: e.target.value,
                  });
                }}
              >
                <MenuItem value={"always_invoice"}>Always Invoice</MenuItem>
                <MenuItem value={"create_prorations"}>
                  Create Prorations
                </MenuItem>
                <MenuItem value={"none"}>None</MenuItem>
              </IgSelect>
            </Grid>
            <Grid item mt={"auto"} pb={1}>
              <IgIconButton
                sx={{ marginLeft: "10px" }}
                aria-label="info"
                size={"small"}
                onClick={() => setHelp((draft) => !draft)}
              >
                <Info size={16} />
              </IgIconButton>
            </Grid>

            {help ? (
              <Grid container item mt={2} direction={"column"}>
                <Grid item>
                  <IgTypography variant={"body2"}>
                    <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
                      {" "}
                      Always Invoice:
                    </IgTypography>{" "}
                    Always invoice immediately for prorations.
                  </IgTypography>
                </Grid>
                <Grid item>
                  <IgTypography variant={"body2"}>
                    <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
                      {" "}
                      Create Prorations:
                    </IgTypography>{" "}
                    Will cause proration invoice items to be created when
                    applicable. These proration items will only be invoiced
                    immediately under{" "}
                    <IgLink
                      variant={"body2"}
                      onClick={() =>
                        window.open(
                          "https://docs.stripe.com/subscriptions/upgrading-downgrading#immediate-payment",
                          "_blank"
                        )
                      }
                    >
                      certain conditions
                    </IgLink>
                    .
                  </IgTypography>
                </Grid>
                <Grid item>
                  <IgTypography variant={"body2"}>
                    <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
                      {" "}
                      None:
                    </IgTypography>{" "}
                    Disable creating prorations in this request.
                  </IgTypography>
                </Grid>
              </Grid>
            ) : null}
          </Grid>
        ) : null}
        {payload.mode === "create" ? (
          <SaveRow payload={payload} dispatchPayload={dispatchPayload} />
        ) : null}
      </Grid>
    </LocalizationProvider>
  );
};
